import React, { useCallback, useMemo, useRef } from 'react';
import type { InternalTableSchema, InternalActionDef } from '../core/schema/types';
import { ActionRegistry, type ActionExecutionContext } from '../registry/ActionRegistry';
import { resolveImportExportSemanticId } from '../utils/importExportSemanticIds';
import type { ActionContext, PendingConfirmation } from './useActionExecutor';
import type { EventScope } from '@packages/block-events/core';
import { mergeActionExecutionParams } from '../utils/mergeActionExecutionParams';

interface UseTableActionsParams {
  internalSchema: InternalTableSchema;
  selectedKeysValue: string[];
  isAllPagesSelected: boolean;
  uncheckedIds: string[];
  effectiveTotalCount: number;
  buildTableQuery: () => Record<string, unknown>;
  executeAction: (action: InternalActionDef, context: ActionContext) => Promise<PendingConfirmation | null>;
  setPendingConfirm: (confirm: PendingConfirmation | null) => void;
  registryRefreshTable: () => void;
  axiosInstance?: any;
  handleLegacyImportFile: (a: InternalActionDef, event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  eventScope?: EventScope | null;
  tableSemanticId?: string;
  publishActionExecuted?: (actionId: string, result?: unknown, selectedKeys?: string[]) => void;
  actionAmbientParams?: Record<string, unknown>;
  /** Active table filter state — merged into OOP action params (embedded parent ids). */
  tableFilters?: Record<string, unknown>;
}

interface UseTableActionsReturn {
  buildActionContext: (base: Partial<ActionContext> & { actionDef: InternalActionDef }) => ActionContext;
  handleRowActionExec: (
    action: InternalActionDef,
    row: Record<string, unknown>,
    subRowContext?: {
      parentRow: Record<string, unknown>;
      parentRowKey: string;
      subIndex: number;
      isSubRow: true;
    }
  ) => Promise<void>;
  v2TemplateDatas: any[];
  hasImportExportActions: boolean;
}

export function useTableActions({
  internalSchema,
  selectedKeysValue,
  isAllPagesSelected,
  uncheckedIds,
  effectiveTotalCount,
  buildTableQuery,
  executeAction,
  setPendingConfirm,
  registryRefreshTable,
  axiosInstance,
  handleLegacyImportFile,
  eventScope,
  tableSemanticId,
  publishActionExecuted,
  actionAmbientParams,
  tableFilters,
}: UseTableActionsParams): UseTableActionsReturn {
  const buildOopActionParams = useCallback(
    () =>
      mergeActionExecutionParams({
        tableFilters,
        ambient: actionAmbientParams,
      }),
    [tableFilters, actionAmbientParams],
  );
  const effectiveTotalCountRef = useRef<number>(effectiveTotalCount);
  effectiveTotalCountRef.current = effectiveTotalCount;

  const buildActionContext = useCallback(
    (base: Partial<ActionContext> & { actionDef: InternalActionDef }): ActionContext => ({
      ...base,
      selectAllActive: isAllPagesSelected,
      deselectedRowIds: isAllPagesSelected ? uncheckedIds : undefined,
      tableQuery: isAllPagesSelected ? buildTableQuery() : undefined,
      totalCount: isAllPagesSelected ? effectiveTotalCountRef.current : undefined,
    }),
    [isAllPagesSelected, uncheckedIds, buildTableQuery]
  );

  const handleRowActionExec = useCallback(
    async (
      action: InternalActionDef,
      row: Record<string, unknown>,
      subRowContext?: {
        parentRow: Record<string, unknown>;
        parentRowKey: string;
        subIndex: number;
        isSubRow: true;
      }
    ) => {
      const rowIdField = internalSchema.dataSource?.rowIdField ?? 'id';
      const execContext: ActionExecutionContext = {
        pk: String(row._resolvedRowId ?? row[rowIdField] ?? ''),
        row: subRowContext ? { ...row, __subRowContext: subRowContext } : row,
        selectedKeys: selectedKeysValue,
        tableQuery: buildTableQuery(),
        refreshTable: registryRefreshTable,
        axiosInstance,
        eventScope: eventScope ?? undefined,
        tableSemanticId,
        params: buildOopActionParams(),
      };

      if (ActionRegistry.hasAction(action.id)) {
        await ActionRegistry.executeAction(action.id, execContext);
        publishActionExecuted?.(action.id, undefined, selectedKeysValue);
        return;
      }

      const context = buildActionContext({
        row,
        selectedKeys: selectedKeysValue,
        actionDef: action,
        ...(subRowContext && {
          parentRow: subRowContext.parentRow,
          parentRowKey: subRowContext.parentRowKey,
          subIndex: subRowContext.subIndex,
          isSubRow: true,
        }),
      });
      const confirmation = await executeAction(action, context);
      if (confirmation) {
        setPendingConfirm(confirmation);
      } else {
        publishActionExecuted?.(action.id, undefined, selectedKeysValue);
      }
    },
    [internalSchema.dataSource, registryRefreshTable, selectedKeysValue, isAllPagesSelected, executeAction, buildActionContext, buildTableQuery, setPendingConfirm, axiosInstance, eventScope, tableSemanticId, publishActionExecuted, buildOopActionParams]
  );

  const secondary = internalSchema.actions.toolbar.secondary;
  const toolbarTemplates = internalSchema.actions.toolbar.templates;

  const legacyExportActions = toolbarTemplates.length === 0
    ? secondary.filter((a) => a.actionType === 'export')
    : [];
  const legacyImportActions = toolbarTemplates.length === 0
    ? secondary.filter((a) => a.actionType === 'import')
    : [];
  const hasImportExportActions = toolbarTemplates.length > 0
    || legacyExportActions.length > 0
    || legacyImportActions.length > 0;

  const v2TemplateDatas = useMemo(() => {
    const result: any[] = [];

    if (toolbarTemplates.length > 0) {
      for (const group of toolbarTemplates) {
        if (group.type === 'download') {
          if (group.children.length > 1) {
            result.push({
              title: group.label,
              groupType: 'download',
              semanticId: resolveImportExportSemanticId(group.semanticId, group.label),
              options: group.children.map((a) => ({
                title: a.label,
                semanticId: resolveImportExportSemanticId(a.semanticId, a.label),
                download: async () => {
                  if (ActionRegistry.hasAction(a.id)) {
                    const execCtx: ActionExecutionContext = {
                      pk: '', row: {},
                      selectedKeys: selectedKeysValue,
                      refreshTable: registryRefreshTable,
                      axiosInstance,
                      eventScope: eventScope ?? undefined,
                      tableSemanticId,
                      params: buildOopActionParams(),
                    };
                    await ActionRegistry.executeAction(a.id, execCtx);
                    return;
                  }
                  const ctx = buildActionContext({ selectedKeys: selectedKeysValue, actionDef: a });
                  const confirmation = await executeAction(a, ctx);
                  if (confirmation) setPendingConfirm(confirmation);
                },
              })),
            });
          } else if (group.children.length === 1) {
            const a = group.children[0];
            result.push({
              title: a.label,
              groupType: 'download',
              semanticId: resolveImportExportSemanticId(
                group.semanticId ?? a.semanticId,
                a.label,
              ),
              download: async () => {
                if (ActionRegistry.hasAction(a.id)) {
                  const execCtx: ActionExecutionContext = {
                    pk: '', row: {},
                    selectedKeys: selectedKeysValue,
                    refreshTable: registryRefreshTable,
                    axiosInstance,
                    eventScope: eventScope ?? undefined,
                    tableSemanticId,
                    params: buildOopActionParams(),
                  };
                  await ActionRegistry.executeAction(a.id, execCtx);
                  return;
                }
                const ctx = buildActionContext({ selectedKeys: selectedKeysValue, actionDef: a });
                const confirmation = await executeAction(a, ctx);
                if (confirmation) setPendingConfirm(confirmation);
              },
            });
          }
        } else if (group.type === 'upload') {
          const buildUploadHandler = (a: InternalActionDef) => ({
            title: a.label,
            groupType: 'upload',
            semanticId: resolveImportExportSemanticId(a.semanticId, a.label),
            import: async (event: React.ChangeEvent<HTMLInputElement>) => {
              const file = event.target.files?.[0];
              if (!file) return;

              if (ActionRegistry.hasAction(a.id)) {
                const execCtx: ActionExecutionContext = {
                  pk: '',
                  row: {},
                  selectedKeys: selectedKeysValue,
                  refreshTable: registryRefreshTable,
                  axiosInstance,
                  file,
                  eventScope: eventScope ?? undefined,
                  tableSemanticId,
                  params: buildOopActionParams(),
                };
                await ActionRegistry.executeAction(a.id, execCtx);
                return;
              }

              await handleLegacyImportFile(a, event);
            },
          });

          if (group.children.length > 1) {
            result.push({
              title: group.label,
              groupType: 'upload',
              semanticId: resolveImportExportSemanticId(group.semanticId, group.label),
              options: group.children.map(buildUploadHandler),
            });
          } else if (group.children.length === 1) {
            result.push(buildUploadHandler(group.children[0]));
          }
        }
      }
      return result;
    }

    if (legacyExportActions.length > 1) {
      result.push({
        title: 'Download Template',
        semanticId: resolveImportExportSemanticId(null, 'Download Template'),
        options: legacyExportActions.map((a) => ({
          title: a.label,
          semanticId: resolveImportExportSemanticId(a.semanticId, a.label),
          download: async () => {
            const ctx = buildActionContext({ selectedKeys: selectedKeysValue, actionDef: a });
            const confirmation = await executeAction(a, ctx);
            if (confirmation) setPendingConfirm(confirmation);
          },
        })),
      });
    } else if (legacyExportActions.length === 1) {
      const a = legacyExportActions[0];
      result.push({
        title: a.label,
        semanticId: resolveImportExportSemanticId(a.semanticId, a.label),
        download: async () => {
          const ctx = buildActionContext({ selectedKeys: selectedKeysValue, actionDef: a });
          const confirmation = await executeAction(a, ctx);
          if (confirmation) setPendingConfirm(confirmation);
        },
      });
    }

    if (legacyImportActions.length > 1) {
      result.push({
        title: 'Import Template',
        semanticId: resolveImportExportSemanticId(null, 'Import Template'),
        options: legacyImportActions.map((a) => ({
          title: a.label,
          semanticId: resolveImportExportSemanticId(a.semanticId, a.label),
          import: (event: React.ChangeEvent<HTMLInputElement>) => handleLegacyImportFile(a, event),
        })),
      });
    } else if (legacyImportActions.length === 1) {
      const a = legacyImportActions[0];
      result.push({
        title: a.label,
        semanticId: resolveImportExportSemanticId(a.semanticId, a.label),
        import: (event: React.ChangeEvent<HTMLInputElement>) => handleLegacyImportFile(a, event),
      });
    }

    return result;
  }, [
    toolbarTemplates,
    legacyExportActions,
    legacyImportActions,
    handleLegacyImportFile,
    buildActionContext,
    selectedKeysValue,
    executeAction,
    setPendingConfirm,
    axiosInstance,
    eventScope,
    tableSemanticId,
    registryRefreshTable,
    buildOopActionParams,
  ]);

  return {
    buildActionContext,
    handleRowActionExec,
    v2TemplateDatas,
    hasImportExportActions,
  };
}
